import apiClient from './api';
import type { PackageType } from './software-update-package.service';
import type { DeviceUpdateEventType } from './software-update-config';

export type RolloutStatus = 'draft' | 'active' | 'archived';
export type RolloutType = 'app' | 'os' | 'fw';

export interface RolloutPackage {
  id: string;
  package_id: string;
  display_name: string;
  package_name: string;
  type: PackageType;
  version: string;
  file: string;
  sort_order: number;
  allow_downgrade: boolean;
  /** Set when the underlying package is soft-deleted; null/absent = live. */
  deleted_at?: string | null;
}

export interface PackageSlot {
  display_name: string;
  package_name: string;
  type: PackageType;
  sort_order: number;
}

/**
 * Independent rollout target filters. Each is optional (null = wildcard / "Any");
 * at least one must be present. These replace the old combined `device_model`.
 */
export interface RolloutTarget {
  machine_type: string | null;
  machine_sub_type: string | null;
  ats_id: string | null;
}

/** Canonical option lists for the target selects, served statically. */
export interface RolloutTargetOptions {
  machine_types: string[];
  machine_sub_types: string[];
  ats_ids: string[];
}

export interface Rollout {
  id: string;
  name: string;
  description: string;
  target: RolloutTarget;
  /** Server-built label for the target, e.g. "Rower / ATS2". */
  target_display_name: string;
  group_id: string | null;
  type: RolloutType;
  /** App-only device gate; null = no condition. Format major.minor.patch. */
  minimum_os_version: string | null;
  status: RolloutStatus;
  packages: RolloutPackage[];
  created_at: string;
  updated_at: string;
  active_at: string | null;
  archived_at: string | null;
}

export interface CreateRolloutRequest {
  name: string;
  description: string;
  target: RolloutTarget;
  group_id: string | null;
  type: RolloutType;
  // App-only device gate. null = no condition. Always send null for os/fw.
  minimum_os_version: string | null;
  packages: Array<{
    package_id: string;
    allow_downgrade?: boolean;
  }>;
}

export interface UpdateRolloutRequest {
  name: string;
  description: string;
  target: RolloutTarget;
  group_id: string | null;
  type: RolloutType;
  // Omitted is treated as null by the backend, so always send the current value.
  minimum_os_version: string | null;
  packages: Array<{
    package_id: string;
    allow_downgrade?: boolean;
  }>;
}

export interface ListRolloutsParams {
  status?: RolloutStatus;
  group_id?: string;
  // Independent target filters. Each matches the stored rollout target value
  // exactly (no device-match simulation, no wildcard expansion).
  machine_type?: string;
  machine_sub_type?: string;
  ats_id?: string;
  type?: RolloutType;
}

export interface ListPackageSlotsParams {
  type: RolloutType;
}

export interface RolloutPackageEventStats {
  rollout_package_id: string;
  package_id: string;
  display_name: string;
  package_name: string;
  version: string;
  sort_order: number;
  allow_downgrade: boolean;
  install_success_count: number;
}

export interface RolloutDetail {
  rollout: Rollout;
  packages: RolloutPackageEventStats[];
}

export interface RolloutEvent {
  id: string;
  rollout_id: string;
  device_id: string;
  machine_id: string;
  serial_number: string;
  // Ownership flag from the device roster join; drives the InternalDeviceIcon.
  is_internal: boolean;
  package_id: string | null;
  package_display_name: string;
  package_name: string;
  package_type: PackageType | '';
  package_version: string;
  from_version: string | null;
  event_type: DeviceUpdateEventType | string;
  reason: string | null;
  progress_percent: number | null;
  server_created_at: string;
  client_created_at: string | null;
}

export interface RolloutEventsResult {
  events: RolloutEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListRolloutEventsParams {
  event_type?: string;
  package_id?: string;
  reason?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export interface ListRolloutEventsTimeseriesParams {
  since: string;
  until: string;
  event_type?: string;
  package_id?: string;
  reason?: string;
  group_by?: 'reason';
}

export interface RolloutEventsTimeseriesSeries {
  event_type: string;
  reason: string | null;
  count: number;
}

export interface RolloutEventsTimeseriesBucket {
  time: string;
  download_success: number;
  download_failed: number;
  download_rejected: number;
  install_success: number;
  install_failed: number;
  // Present when the timeseries is requested with group_by=reason.
  series?: RolloutEventsTimeseriesSeries[];
}

export interface RolloutEventsTimeseries {
  bucket_size: string;
  start: string;
  end: string;
  buckets: RolloutEventsTimeseriesBucket[];
}

export interface DevicePackage {
  display_name: string;
  package_name: string;
  type: PackageType | string;
  version: string;
  created_at: string;
}

export interface DeviceSoftwareUpdateRollout {
  id: string;
  name: string;
  status: RolloutStatus;
  first_event_at: string;
  last_event_at: string;
  event_count: number;
}

export interface DeviceSoftwareUpdateEvent {
  id: string;
  rollout_id: string;
  rollout_name: string;
  device_id: string;
  package_display_name: string;
  package_name: string;
  package_type: PackageType | '';
  package_version: string;
  from_version: string | null;
  event_type: DeviceUpdateEventType | string;
  reason: string | null;
  progress_percent: number | null;
  server_created_at: string;
  client_created_at: string | null;
}

export interface DeviceSoftwareUpdateEventList {
  events: DeviceSoftwareUpdateEvent[];
  total: number;
  limit: number;
  offset: number;
  newer_count: number;
}

export interface DeviceSoftwareUpdateGroup {
  id: string;
  name: string;
}

export interface DeviceSoftwareUpdateDetail {
  packages: DevicePackage[];
  packages_updated_at: string | null;
  rollouts: DeviceSoftwareUpdateRollout[];
  events: DeviceSoftwareUpdateEventList;
  // The one static group this device belongs to, or null when ungrouped.
  group: DeviceSoftwareUpdateGroup | null;
}

export interface DeviceSoftwareUpdatePackageFilter {
  type: PackageType;
  package_name: string;
}

export interface ListDeviceSoftwareUpdateParams {
  rollout_id?: string;
  event_type?: string;
  package?: DeviceSoftwareUpdatePackageFilter;
  before?: string;
  exclude_progress?: boolean;
  limit?: number;
  offset?: number;
}

const base = '/admin/software-update/rollouts';
const packageSlotsBase = '/admin/software-update/package-slots';
const rolloutTargetOptionsBase = '/admin/software-update/rollout-target-options';

function buildQuery(params: ListRolloutsParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.group_id) search.set('group_id', params.group_id);
  if (params.machine_type) search.set('machine_type', params.machine_type);
  if (params.machine_sub_type) search.set('machine_sub_type', params.machine_sub_type);
  if (params.ats_id) search.set('ats_id', params.ats_id);
  if (params.type) search.set('type', params.type);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const softwareUpdateRolloutService = {
  listPackageSlots: async (params: ListPackageSlotsParams): Promise<PackageSlot[]> => {
    // Slots depend only on rollout type; no target/device_model param.
    const search = new URLSearchParams();
    search.set('type', params.type);
    return apiClient.get<PackageSlot[]>(`${packageSlotsBase}?${search.toString()}`);
  },
  getRolloutTargetOptions: async (): Promise<RolloutTargetOptions> => {
    return apiClient.get<RolloutTargetOptions>(rolloutTargetOptionsBase);
  },
  list: async (params: ListRolloutsParams = {}): Promise<Rollout[]> => {
    return apiClient.get<Rollout[]>(`${base}${buildQuery(params)}`);
  },
  get: async (id: string): Promise<Rollout> => {
    return apiClient.get<Rollout>(`${base}/${id}`);
  },
  create: async (data: CreateRolloutRequest): Promise<Rollout> => {
    return apiClient.post<Rollout>(base, data);
  },
  update: async (id: string, data: UpdateRolloutRequest): Promise<Rollout> => {
    return apiClient.put<Rollout>(`${base}/${id}`, data);
  },
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`${base}/${id}`);
  },
  deploy: async (id: string): Promise<Rollout> => {
    return apiClient.post<Rollout>(`${base}/${id}/deploy`, {});
  },
  archive: async (id: string): Promise<Rollout> => {
    return apiClient.post<Rollout>(`${base}/${id}/archive`, {});
  },
  deployOSRecovery: async (id: string): Promise<Rollout> => {
    return apiClient.post<Rollout>(`${base}/${id}/deploy-os-recovery`, {});
  },
  getDetail: async (id: string): Promise<RolloutDetail> => {
    return apiClient.get<RolloutDetail>(`${base}/${id}/detail`);
  },
  listEvents: async (
    id: string,
    params: ListRolloutEventsParams = {},
  ): Promise<RolloutEventsResult> => {
    const search = new URLSearchParams();
    if (params.event_type) search.set('event_type', params.event_type);
    if (params.package_id) search.set('package_id', params.package_id);
    if (params.reason) search.set('reason', params.reason);
    if (params.since) search.set('since', params.since);
    if (params.until) search.set('until', params.until);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return apiClient.get<RolloutEventsResult>(
      `${base}/${id}/events${qs ? `?${qs}` : ''}`,
    );
  },
  getEventsTimeseries: async (
    id: string,
    params: ListRolloutEventsTimeseriesParams,
  ): Promise<RolloutEventsTimeseries> => {
    const search = new URLSearchParams();
    search.set('since', params.since);
    search.set('until', params.until);
    if (params.event_type) search.set('event_type', params.event_type);
    if (params.package_id) search.set('package_id', params.package_id);
    if (params.reason) search.set('reason', params.reason);
    if (params.group_by) search.set('group_by', params.group_by);
    return apiClient.get<RolloutEventsTimeseries>(
      `${base}/${id}/events/timeseries?${search.toString()}`,
    );
  },
  getDeviceSoftwareUpdate: async (
    deviceId: string,
    params: ListDeviceSoftwareUpdateParams = {},
  ): Promise<DeviceSoftwareUpdateDetail> => {
    const search = new URLSearchParams();
    if (params.rollout_id) search.set('rollout_id', params.rollout_id);
    if (params.event_type) search.set('event_type', params.event_type);
    if (params.package) {
      search.set('package_type', params.package.type);
      search.set('package_name', params.package.package_name);
    }
    if (params.before) search.set('before', params.before);
    if (params.exclude_progress) search.set('exclude_progress', 'true');
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return apiClient.get<DeviceSoftwareUpdateDetail>(
      `/devices/${deviceId}/software-update${qs ? `?${qs}` : ''}`,
    );
  },
};

export default softwareUpdateRolloutService;
